import { useMemo } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { inputFilter, selectorFilter, dateFilter } from "../../shared/crud/filter-widgets";
import {
  CHARGE_ALERTS,
  CHARGE_STATUS,
  PAYMENT_TYPES,
  RERSERVATION_STATUS,
} from "../../witadmin/Common/constants";
import { TableFilters } from "../../shared/crud/list";
import { automaticChargesFilterMessages } from "../messages";
import { asUTC } from "../../../lib/dateUtils";
import { parseISO } from "date-fns";
import { GetAutomaticChargeRequest } from "../infraestructure/automaticCharges.api";

const defaultRequest = {
  orderBy: {},
  estimatedCollectDate: "",
  effectiveCollectDate: "",
  bookingId: undefined,
  multipleBookingId: undefined,
  paymentTypes: [],
};

function getRoomStayStatus(status: string): string {
  switch (status) {
    case RERSERVATION_STATUS.OK:
      return "RESERVATION";
    case RERSERVATION_STATUS.PEND:
      return "PRE_RESERVATION";
    case RERSERVATION_STATUS.CXL:
      return "CANCEL";
    case RERSERVATION_STATUS.KO:
      return "KO";
    default:
      console.warn(`Unknown reservation status received: ${status}`);
      return "";
  }
}

function getChargeStatus(status: string): string {
  switch (status) {
    case CHARGE_STATUS.CXL:
      return "CANCELLED";
    case CHARGE_STATUS.PEND:
      return "PENDING";
    case CHARGE_STATUS.OK:
      return "OK";
    default:
      return "KO";
  }
}

export function generateAutomaticChargeRequest({
  filterValues,
  pageSize,
  currentPage,
}): GetAutomaticChargeRequest {
  const request = {
    estimatedCollectDateFrom: filterValues?.estimatedCollectDate?.startDate
      ? asUTC(parseISO(filterValues.estimatedCollectDate.startDate), "yyyy-MM-dd")
      : defaultRequest.estimatedCollectDate,
    estimatedCollectDateTo: filterValues?.estimatedCollectDate?.endDate
      ? asUTC(parseISO(filterValues.estimatedCollectDate.endDate), "yyyy-MM-dd")
      : defaultRequest.estimatedCollectDate,
    effectiveCollectDateFrom: filterValues?.effectiveCollectDate?.startDate
      ? asUTC(parseISO(filterValues.effectiveCollectDate.startDate), "yyyy-MM-dd")
      : defaultRequest.effectiveCollectDate,
    effectiveCollectDateTo: filterValues?.effectiveCollectDate?.endDate
      ? asUTC(parseISO(filterValues.effectiveCollectDate.endDate), "yyyy-MM-dd")
      : defaultRequest.effectiveCollectDate,
    roomStayId: filterValues?.bookingId || defaultRequest.bookingId,
    reservationId: filterValues?.multipleBookingId || defaultRequest.multipleBookingId,
    currency: filterValues?.currency === "original" ? "" : filterValues?.currency,
    paymentType: filterValues?.paymentType || undefined,
    amount: filterValues.amount ? Number(filterValues.amount) : 0,
    roomStayStatus: filterValues.status ? getRoomStayStatus(filterValues.status) : undefined,
    chargeStatus: filterValues.chargeStatus
      ? getChargeStatus(filterValues.chargeStatus)
      : undefined,
    chargeAlert:
      Array.isArray(filterValues.chargeAlert) && filterValues.chargeAlert.length
        ? filterValues.chargeAlert[0]
        : undefined,
    cancellationPolicyTicker: filterValues?.cancellationPolicy || undefined,
  };

  const filteredRequest = Object.fromEntries(
    Object.entries(request).filter(
      ([_, value]) => value !== undefined && value !== "" && value !== 0
    )
  );

  return {
    request: filteredRequest,
    size: pageSize,
    page: currentPage,
  };
}

export function useAutomaticChargeFilters() {
  const intl = useIntl();

  const chargeStatusOptions = useMemo(
    () =>
      Object.keys(CHARGE_STATUS).map(key => ({
        value: CHARGE_STATUS[key],
        label: intl.formatMessage({ id: `paymentStatus.${key}`, defaultMessage: key }),
      })),
    [intl]
  );

  const paymentTypeOptions = useMemo(
    () =>
      Object.keys(PAYMENT_TYPES).map(key => ({
        value: PAYMENT_TYPES[key],
        label: intl.formatMessage({ id: `paymentTypes.${key}`, defaultMessage: key }),
      })),
    [intl]
  );

  const reservationStatusOptions = useMemo(
    () =>
      Object.keys(RERSERVATION_STATUS)
        .filter(status => status !== RERSERVATION_STATUS.KO)
        .map(key => ({
          value: RERSERVATION_STATUS[key],
          label: intl.formatMessage({ id: `reservationStatus.${key}`, defaultMessage: key }),
        })),
    [intl]
  );

  return useMemo(() => {
    const filters: TableFilters<any, any> = {
      bookingId: {
        filterFn: "async",
        widget: ({ ...props }) =>
          inputFilter({
            ...props,
            placeholder: intl.formatMessage(automaticChargesFilterMessages.bookingId),
          }),
      },
      multipleBookingId: {
        filterFn: "async",
        widget: ({ ...props }) =>
          inputFilter({
            ...props,
            placeholder: intl.formatMessage(automaticChargesFilterMessages.multipleBookingId),
          }),
      },
      status: {
        filterFn: "async",
        widget: selectorFilter(
          <FormattedMessage id="automaticCharges.filter.status" defaultMessage="Status" />,
          reservationStatusOptions
        ),
      },
      chargeStatus: {
        filterFn: "async",
        widget: selectorFilter(
          <FormattedMessage
            id="automaticCharges.filter.chargeStatus"
            defaultMessage="Charge Status"
          />,
          chargeStatusOptions
        ),
      },
      paymentType: {
        filterFn: "async",
        widget: selectorFilter(
          <FormattedMessage
            id="automaticCharges.filter.paymentType"
            defaultMessage="Payment Type"
          />,
          paymentTypeOptions
        ),
      },
      cancellationPolicy: {
        filterFn: "async",
        widget: ({ ...props }) =>
          inputFilter({
            ...props,
            placeholder: intl.formatMessage(automaticChargesFilterMessages.cancellationPolicy),
          }),
      },
      estimatedCollectDate: {
        filterFn: "async",
        widget: dateFilter("estimatedCollectDate", intl),
      },
      effectiveCollectDate: {
        filterFn: "async",
        widget: dateFilter("effectiveCollectDate", intl),
        title: automaticChargesFilterMessages.effectiveCollectDate,
      },
      amount: {
        filterFn: "async",
        widget: ({ ...props }) =>
          inputFilter({
            ...props,
            placeholder: intl.formatMessage(automaticChargesFilterMessages.amount),
            type: "number",
          }),
      },
      chargeAlert: {
        filterFn: "async",
        widget: selectorFilter(
          <FormattedMessage id="automaticCharges.filter.alerts" defaultMessage="Alerts" />,
          [
            {
              value: [CHARGE_ALERTS.CANNOT_CHARGED],
              label: intl.formatMessage(
                automaticChargesFilterMessages[CHARGE_ALERTS.CANNOT_CHARGED]
              ),
            },
            {
              value: [CHARGE_ALERTS.MODIFIED_BY_RESERVATION],
              label: intl.formatMessage(
                automaticChargesFilterMessages[CHARGE_ALERTS.MODIFIED_BY_RESERVATION]
              ),
            },
            {
              value: [CHARGE_ALERTS.CHARGE_FAILED],
              label: intl.formatMessage(
                automaticChargesFilterMessages[CHARGE_ALERTS.CHARGE_FAILED]
              ),
            },
          ]
        ),
      },
    };

    return filters;
  }, [intl, chargeStatusOptions, paymentTypeOptions, reservationStatusOptions]);
}
